import fp from 'lodash/fp';
import React, { useRef, useMemo, useCallback, useState, MouseEventHandler, ComponentProps } from 'react';
import { VariableSizeGrid, GridChildComponentProps } from 'react-window';
import scrollbarSize from 'dom-helpers/scrollbarSize';

import { Text, multipleRef, cn } from 'Modules/ui';

import { BODY_CELL_CLASSNAME } from '../constants';
import { cellStyles } from '../Cells';
import { GridAdapter } from '../GridAdapter';
import { CheckableColumn } from '../CheckableColumn';
import { BodyProps, CellItemData } from './types';
import styles from './index.module.scss';


export * as BodyTypes from './types';

const cellRenderer = React.memo<GridChildComponentProps<CellItemData>>(({
    rowIndex, columnIndex,
    style,
    data: {
        columns, checkable, getData, selectedRow, hoveredRowIndex,
        setHoveredRowIndex, onRowClick, className,
        rowCount,
    },
}) => {
    const { Header, Cell, className: cellClassName, ...column } = columns[columnIndex];
    const data = getData(rowIndex);
    const isSelected = selectedRow === rowIndex;
    const isHovered = hoveredRowIndex === rowIndex;
    return (
        <Text<ComponentProps<'div'>>
            component='div'
            textType='light'
            data-testid={`row_${rowIndex} cell_${columnIndex}`}
            className={cn(
                cellStyles.cell,
                BODY_CELL_CLASSNAME,
                fp.isFunction(className) ? className({
                    data,
                    columnIndex,
                    rowCount,
                    rowIndex,
                    columnsCount: columns.length,
                    isFirstColumn: columnIndex === 0,
                    isLastColumn: columnIndex === columns.length - 1,
                    isRowLast: rowIndex === rowCount - 1,
                }) : className,
                !checkable && columnIndex === 0 && cellStyles.first,
                columnIndex === columns.length - 1 && cellStyles.last,
                onRowClick && styles.clickableRow,
                isSelected && styles.selected,
            )}
            role='presentation'
            onClick={useCallback(() => (onRowClick || fp.noop)({
                rowData: data,
                rowNumber: rowIndex,
            }), [data, onRowClick, rowIndex])}
            onMouseEnter={useCallback(
                () => setHoveredRowIndex(rowIndex),
                [rowIndex, setHoveredRowIndex],
            )}
            onMouseLeave={useCallback<MouseEventHandler>(
                ({ relatedTarget }) => {
                    const element = relatedTarget as Element;
                    return !element.classList || element.classList.contains(BODY_CELL_CLASSNAME)
                        ? null
                        : setHoveredRowIndex(null);
                },
                [setHoveredRowIndex],
            )}
            style={style as React.CSSProperties}
        >
            {data && (
                <div className={cn(styles.content, cellClassName)}>
                    <Cell
                        data={data}
                        column={column}
                        isSelected={isSelected}
                        isHovered={isHovered}
                    />
                </div>
            )}
        </Text>
    );
});

export const Body = React.forwardRef<VariableSizeGrid, BodyProps>(({
    top,
    width,
    height,
    hScrollBarVisible,
    checkable,
    checkboxRef,
    checkboxWidth,
    checkboxCell,
    checkboxComponent,
    checkedKeySelector,
    checkedRows,
    onCheckedChanged,
    disabled,
    rowCount,
    rowHeight,
    isHovering,
    selectedRow,
    onRowClick,
    columns,
    getData,
    columnWidth,
    onItemsRendered,
    onScroll,
    classNames = { body: undefined },
}, ref) => {
    const bodyRef = useRef<VariableSizeGrid>();

    const [hoveredRowIndex, setHoveredRowIndexCallback] = useState(null);
    const setHoveredRowIndex = useCallback(
        index => (isHovering ? setHoveredRowIndexCallback : fp.noop)(index),
        [isHovering],
    );
    return (
        <>
            {checkable && (
                <CheckableColumn
                    ref={checkboxRef}
                    checkable={checkable}
                    width={checkboxWidth}
                    height={height - (hScrollBarVisible ? scrollbarSize() : 0)}
                    style={{ top }}
                    rowCount={rowCount}
                    rowHeight={rowHeight}
                    checkedKeySelector={checkedKeySelector}
                    checkedRows={checkedRows}
                    onCheckedChanged={onCheckedChanged}
                    className={classNames.checkbox}
                    disabled={disabled}
                    setHoveredRowIndex={setHoveredRowIndex}
                    selectedRow={onRowClick ? selectedRow : undefined}
                    checkboxContainer={checkboxCell}
                    checkbox={checkboxComponent}
                    getData={getData}
                />
            )}
            <GridAdapter
                ref={multipleRef(ref, bodyRef)}
                className={cn(
                    styles.body,
                    styles.vScroll,
                    hScrollBarVisible && styles.hScroll,
                    classNames.body.container,
                )}
                width={width}
                height={height}
                style={{
                    left: checkable ? checkboxWidth : 0,
                    top,
                }}
                rowCount={rowCount}
                rowHeight={rowHeight}
                columnCount={columns.length}
                columnWidth={columnWidth}
                onScroll={onScroll}
                onItemsRendered={onItemsRendered}
                itemData={useMemo<CellItemData>(
                    () => ({
                        columns,
                        checkable: !!checkable,
                        getData,
                        selectedRow,
                        hoveredRowIndex,
                        setHoveredRowIndex,
                        onRowClick,
                        className: classNames.body.cell,
                        rowCount,
                    }),
                    [
                        columns,
                        checkable,
                        getData,
                        selectedRow,
                        hoveredRowIndex,
                        setHoveredRowIndex,
                        onRowClick,
                        classNames.body.cell,
                        rowCount,
                    ],
                )}
            >
                {cellRenderer}
            </GridAdapter>
        </>
    );
});
